/**
 * Money is always stored and computed as integer cents. Never use floating
 * point for currency. These helpers are the only place amounts are parsed or
 * formatted.
 */

export type Cents = number;

export function assertCents(value: number): asserts value is Cents {
  if (!Number.isSafeInteger(value)) {
    throw new Error(`Invalid cents value: ${value}`);
  }
}

/** Parses a user-entered dollar string like "1,234.56" or "-$12.5" into cents. */
export function parseDollars(input: string): Cents {
  const cleaned = input.trim().replace(/[$,\s]/g, "");
  const match = /^(-)?(\d+)(?:\.(\d{0,2}))?$/.exec(cleaned);
  if (!match) throw new Error(`Not a valid dollar amount: "${input}"`);
  const [, sign, whole, frac = ""] = match;
  const cents = Number(whole) * 100 + Number(frac.padEnd(2, "0"));
  const result = sign ? -cents : cents;
  assertCents(result);
  return result;
}

export function formatCents(cents: Cents, opts: { showCents?: boolean } = {}): string {
  assertCents(cents);
  const showCents = opts.showCents ?? true;
  const negative = cents < 0;
  const abs = Math.abs(cents);
  const dollars = Math.floor(abs / 100).toLocaleString("en-US");
  const rest = String(abs % 100).padStart(2, "0");
  const body = showCents ? `$${dollars}.${rest}` : `$${Math.round(abs / 100).toLocaleString("en-US")}`;
  return negative ? `-${body}` : body;
}

export function sumCents(values: readonly Cents[]): Cents {
  let total = 0;
  for (const v of values) {
    assertCents(v);
    total += v;
  }
  assertCents(total);
  return total;
}
